import {
  AuthResponse,
  CurrentUser,
  CurrentUserBusinessRef,
  LoginRequest,
  RegisterBuyerRequest,
  RegisterBuyerResult,
} from '../types';
import { delay } from './delay';
import { ERR_INVALID_CREDENTIALS, ERR_VALIDATION } from './errors';
import { db, findActiveBusinessByUserId } from './db';
import { MOCK_BUYER_ACCOUNTS, MockBuyerAccount } from './fixtures/users';

const buildCurrentUser = (account: MockBuyerAccount): CurrentUser => {
  let currentBusiness: CurrentUserBusinessRef | null = null;
  const found = findActiveBusinessByUserId(account.userId);
  if (found) {
    currentBusiness = {
      id: found.business.id,
      name: found.business.name,
      role: found.membership.role,
    };
  }
  return {
    id: account.userId,
    email: account.email,
    fullName: account.fullName,
    phone: account.phone,
    role: 'BUYER_USER',
    status: account.status,
    currentBusiness,
    defaultShippingAddress: account.defaultShippingAddress ?? null,
    defaultBillingAddress: account.defaultBillingAddress ?? null,
  };
};

const issueToken = (userId: string): string =>
  `mock.${userId}.${Date.now().toString(36)}`;

const issueTokenBundle = (userId: string) => ({
  accessToken: issueToken(userId),
  refreshToken: issueToken(userId + '.refresh'),
  expiresIn: 60 * 60,
});

const findAccountByEmail = (email: string): MockBuyerAccount | undefined =>
  MOCK_BUYER_ACCOUNTS.find(
    (a) => a.email.toLowerCase() === email.trim().toLowerCase(),
  );

export const authMock = {
  login: async (req: LoginRequest): Promise<AuthResponse> => {
    await delay();
    const account = findAccountByEmail(req.email);
    if (!account || account.password !== req.password) {
      throw ERR_INVALID_CREDENTIALS();
    }
    const user = buildCurrentUser(account);
    const tokens = issueTokenBundle(account.userId);
    return { ...tokens, user };
  },

  register: async (req: RegisterBuyerRequest): Promise<RegisterBuyerResult> => {
    await delay(200, 500);
    if (findAccountByEmail(req.email)) {
      throw ERR_VALIDATION('Email already in use');
    }
    if (req.createBusiness && !req.business) {
      throw ERR_VALIDATION('Business details are required when createBusiness=true');
    }
    const userId = `u-${Date.now().toString(36)}`;
    const account: MockBuyerAccount = {
      email: req.email.trim(),
      password: req.password,
      userId,
      fullName: req.fullName,
      phone: req.phone,
      defaultShippingAddress: req.defaultShippingAddress,
      defaultBillingAddress: req.defaultBillingAddress,
      status: 'ACTIVE',
    };
    MOCK_BUYER_ACCOUNTS.push(account);

    let createdBusinessId: string | undefined;
    if (req.createBusiness && req.business) {
      const business = {
        id: `biz-${Date.now().toString(36)}`,
        ...req.business,
        status: 'ACTIVE' as const,
        ownerUserId: userId,
      };
      db.businesses.push(business);
      db.memberships.push({
        id: `mem-${Date.now().toString(36)}`,
        businessId: business.id,
        userId,
        role: 'OWNER',
        status: 'ACTIVE',
        joinedAt: new Date().toISOString(),
      });
      createdBusinessId = business.id;
    }
    return {
      userId,
      createdBusinessId,
      createdBusinessRole: createdBusinessId ? 'OWNER' : undefined,
      status: 'ACTIVE',
      emailVerificationRequired: false,
    };
  },

  me: async (token: string): Promise<CurrentUser> => {
    await delay(50, 150);
    const userId = token.split('.')[1];
    const account = MOCK_BUYER_ACCOUNTS.find((a) => a.userId === userId);
    if (!account) throw ERR_INVALID_CREDENTIALS();
    return buildCurrentUser(account);
  },

  logout: async (): Promise<void> => {
    await delay(50, 150);
  },
};
